import { ActionsMenu } from 'melp-design/components';
import { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalContext } from 'store/modal';
import {
  useDeleteSurvey,
  useSendSurveyNotifications,
  SurveyResults,
  useSurveyGroupAssignments,
  BaseSurvey,
  getSurveyPermissionName,
  Survey,
} from 'store/surveys';
import { usePredefinedToasts } from 'utils/Toast';
import { useConfirm } from 'store/confirm';
import { useDownloadPdf } from 'store/files';
import { toast } from 'react-toastify';
import { LanguageSelectModal, ShareModal } from 'components/modals';
import { generateDynamicLink, resolveTranslation } from 'utils/general';
import {
  CopySurveyModal,
  EditEndDateModal,
  parsedQuestionResults,
  retriesToAttempts,
  SurveyResultsDocument,
  SurveyWebAccessModal,
  useExportSurveyResultsJob,
} from 'containers/surveys';
import { useSurveyNewsGenerator } from 'state/SurveyNews/SurveyNews';
import { ROUTES } from 'config';
import { useHistory } from 'react-router-dom';
import { formatList, formatPercents } from 'utils/format';
import moment from 'moment';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { usePointsLabel } from 'store/recognitions';

interface Props {
  survey: BaseSurvey & {
    translations?: Survey['translations'];
    questions?: Survey['questions'];
  };
  results?: SurveyResults;
  type?: ComponentProps<typeof ActionsMenu>['type'];
}

export const SurveyActions: FC<Props> = ({ survey, results, type }) => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const history = useHistory();
  const { hasEditPermission } = useMe();
  const { defaultLanguage } = useCompanyLanguages();
  const predefinedToasts = usePredefinedToasts();
  const pointsLabel = usePointsLabel();
  const hasSurveyEditPermission = hasEditPermission([
    getSurveyPermissionName(survey.type),
  ]);

  const { data: assignments } = useSurveyGroupAssignments(
    results ? survey.id : '',
  );

  const { mutate: sendSurveyNotifications } = useSendSurveyNotifications();
  const { exportSurveyResults, isExporting } = useExportSurveyResultsJob();
  const { mutate: downloadPdf } = useDownloadPdf();
  const { mutate: deleteSurvey, isLoading: isDeleting } = useDeleteSurvey();

  const newsGenerator = useSurveyNewsGenerator(results?.id ?? '');

  const handleGenerateNews = async () => {
    const generatedNews = await newsGenerator.generate();
    predefinedToasts.success.added();
    history.push(
      `${ROUTES.news.details.replace(':id', generatedNews.id)}?guided=true`,
    );
  };

  return (
    <ActionsMenu
      type={type}
      items={[
        ...(survey.status === 'in_progress'
          ? [
              {
                label: t('surveys.sendReminder'),
                disabled: !hasSurveyEditPermission,
                onClick: () => {
                  sendSurveyNotifications(
                    { surveyId: survey.id },
                    {
                      onSuccess: () => {
                        toast.success(t('surveys.reminderSent'));
                      },
                    },
                  );
                },
              },
              {
                label: t('global.actions.share'),
                onClick: () => {
                  openModal({
                    component: ShareModal,
                    props: {
                      url: generateDynamicLink({
                        path: 'survey',
                        params: { surveyId: survey.id },
                      }),
                    },
                  });
                },
              },
            ]
          : []),
        ...(survey.status === 'in_progress' && survey.type === 'survey'
          ? [
              {
                label: t('surveys.actions.allow_web_access'),
                disabled: !hasSurveyEditPermission,
                onClick: () => {
                  openModal({
                    component: SurveyWebAccessModal,
                    props: { surveyId: survey.id },
                  });
                },
              },
            ]
          : []),
        {
          label: t('alternatives.actions.change_deadline'),
          disabled: !hasSurveyEditPermission,
          onClick: () => {
            openModal({
              component: EditEndDateModal,
              props: {
                surveyId: survey.id,
                initialValues: {
                  endsAt: survey.endDateTime ?? '',
                },
              },
            });
          },
        },
        {
          label: t('dashboard.download_xls'),
          onClick: () => {
            exportSurveyResults({ surveyId: survey.id });
          },
          disabled: isExporting,
        },
        ...(results
          ? [
              {
                label: t('common.downloadPDF'),
                onClick: async () => {
                  const response = await openModal({
                    component: LanguageSelectModal,
                    props: {
                      languages:
                        survey.translations?.map(
                          (translation) => translation.language,
                        ) ??
                        results.questions[0]?.translations.map(
                          (translation) => translation.language,
                        ),
                    },
                  });

                  if (response.action === 'CONFIRM') {
                    downloadPdf({
                      filename: `${results.type}-${results.name}`,
                      document: (
                        <SurveyResultsDocument
                          title={results.name}
                          settings={[
                            {
                              label: t('surveys.sentTo'),
                              value: formatList(
                                i18n.language,
                                assignments?.items
                                  .filter(
                                    (assignment) =>
                                      assignment.assignmentStatus === 'on',
                                  )
                                  .map((assignment) => assignment.groupName) ??
                                  [],
                              ),
                            },
                            {
                              label: t('surveys.sentBy'),
                              value: formatList(
                                i18n.language,
                                results.notificationMethods.map((method) =>
                                  t(`news.send_method.${method}`),
                                ),
                              ),
                            },
                            {
                              label: t('surveys.startDate'),
                              value: results.publishDateTime
                                ? moment(results.publishDateTime).format('l LT')
                                : '-',
                            },
                            {
                              label: t('surveys.endDate'),
                              value: results.endDateTime
                                ? moment(results.endDateTime).format('l LT')
                                : '-',
                            },
                            {
                              label: t('surveys.hasWebAccess.label'),
                              value: survey.hasWebAccess
                                ? t('common.yes')
                                : t('common.no'),
                            },
                            ...(results.rewardPoints
                              ? [
                                  {
                                    label: t('surveys.rewardsEnabled.label', {
                                      pointName: pointsLabel,
                                    }),
                                    value: String(results.rewardPoints),
                                  },
                                ]
                              : []),
                            ...(results.type === 'training'
                              ? [
                                  {
                                    label: t('surveys.scoringEnabled.label'),
                                    value: survey.scoringEnabled
                                      ? t('common.yes')
                                      : t('common.no'),
                                  },
                                  ...(survey.scoringEnabled
                                    ? [
                                        {
                                          label: t('surveys.minScore.label'),
                                          value: formatPercents(
                                            i18n.language,
                                            results.minScore / 100,
                                          ),
                                        },
                                        {
                                          label: t('surveys.attempts.label'),
                                          value: String(
                                            retriesToAttempts(results.retries),
                                          ),
                                        },
                                      ]
                                    : []),
                                ]
                              : []),
                          ]}
                          statistics={[
                            {
                              label: t('common.recipients'),
                              value: results.currentRecipients,
                            },
                            {
                              label: t('surveys.completionCount'),
                              value: results.completedBy,
                            },
                            {
                              label: t('surveys.responseRate'),
                              value: formatPercents(
                                i18n.language,
                                results.currentRecipients
                                  ? results.completedBy /
                                      results.currentRecipients
                                  : results.completedBy
                                  ? 1
                                  : 0,
                              ),
                            },
                          ]}
                          intro={
                            survey.translations
                              ? resolveTranslation(
                                  survey.translations,
                                  response.language,
                                  defaultLanguage,
                                )
                              : null
                          }
                          questions={parsedQuestionResults({
                            results,
                            userLng: response.language,
                            defaultLng: defaultLanguage,
                          }).map((question) => ({
                            ...question,
                            isRequired: survey.questions?.find(
                              (item) => item.id === question.id,
                            )?.isRequired,
                          }))}
                        />
                      ),
                    });
                  }
                },
                disabled: isExporting,
              },
            ]
          : []),
        ...(survey.type === 'survey'
          ? [
              {
                label: t('surveys.createNews'),
                disabled: !hasEditPermission(['news']),
                onClick: () => {
                  handleGenerateNews();
                },
              },
            ]
          : []),
        {
          label: t('actions.make_copy'),
          disabled: !hasSurveyEditPermission,
          onClick: () => {
            openModal({
              component: CopySurveyModal,
              props: {
                surveyId: survey.id,
                name: survey.name,
                type: survey.type,
              },
            });
          },
        },
        {
          label: t('actions.delete'),
          onClick: async () => {
            const { confirmed } = await confirmation(
              t('common.areYouSureToDelete', {
                title: survey.name,
              }),
              undefined,
              'danger',
            );

            if (confirmed) {
              deleteSurvey(
                { surveyId: survey.id },
                {
                  onSuccess: () => {
                    predefinedToasts.success.deleted();
                    history.push(ROUTES.surveys[survey.type].list);
                  },
                },
              );
            }
          },
          color: 'error',
          disabled: isDeleting || !hasSurveyEditPermission,
        },
      ]}
    />
  );
};
